import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import CoinDrawerSelect from "components/CoinDrawerSelect";
import { GetWithdrawAmountMax } from "store/api/withdraw";
import { Grid } from "@mui/material";
import WithdrawAmount from "./Components/WithdrawAmount";
import WithdrawNetworkAddress from "./Components/WithdrawNetworkAddress";
import WithdrawSubmit from "./Components/WithdrawSubmit";
import WithdrawTypeSelect from "./Components/WithdrawTypeSelect";
import { checkIfRulesAreAccepted } from "utils/helpers/checkIfRulesAreAccepted";
import http from "config/http";
import { repeat } from "utils/helpers/interval";
import useAlertModal from "hooks/useAlertModal";
import { useFormContext } from "react-hook-form";
import { useMemo } from "react";
import useNotify from "hooks/useNotify";

let getWithdrawAmountMaxInterval = null;

function WithdrawForm({ withdrawInitial, getWithdrawInitial }) {
  const notify = useNotify();
  const navigate = useNavigate();
  const params = useParams();
  const alert = useAlertModal();

  const { watch, setValue, reset } = useFormContext();
  const typeId = watch("typeId");
  const coinId = +params?.id || 1;
  const networkId = watch("networkId");

  const [withdrawAmountMax, setWithdrawAmountMax] = useState();

  const getWithdrawAmountMax = async () => {
    try {
      const { data } = await http.get(GetWithdrawAmountMax({ coinId, typeId }));

      setWithdrawAmountMax(data?.data);
    } catch (error) {
      checkIfRulesAreAccepted({ error, notify, alert, navigate });
    }
  };

  const selectedCoin = useMemo(() => {
    const coin = withdrawInitial?.lisCoin?.find((coin) => coin?.id === coinId);

    setValue("isDigital", coin?.isDigital);

    return coin;
  }, [withdrawInitial?.lisCoin, coinId, setValue]);

  const selectedNetwork = useMemo(() => {
    const network = withdrawInitial?.lisNetwork?.filter(
      (net) => net?.coinId === coinId && net?.typeId === typeId
    );

    if (network) {
      setValue("networkId", network?.[0]?.networkId);
    }

    return network;
  }, [withdrawInitial?.lisNetwork, setValue, coinId, typeId]);

  useEffect(() => {
    if (withdrawInitial?.validSeconds && typeId && coinId) {
      getWithdrawAmountMax();

      clearInterval(getWithdrawAmountMaxInterval);

      getWithdrawAmountMaxInterval = repeat({
        action: () => getWithdrawAmountMax(),
        time: withdrawInitial?.validSeconds * 1000,
      });
    }

    return () => {
      clearInterval(getWithdrawAmountMaxInterval);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withdrawInitial?.validSeconds, typeId, coinId]);

  useEffect(() => {
    reset((formValues) => ({
      ...formValues,
      coinId,
    }));
  }, [coinId, reset]);

  const currentNetworkFields = selectedNetwork?.find(
    (network) => network?.networkId === networkId
  );

  return (
    <>
      <Grid container spacing={2} pb={2}>
        <CoinDrawerSelect
          coinList={withdrawInitial}
          selectedCoin={selectedCoin}
          onCoinClick={(coin) => navigate(`/withdraw/${coin?.id}`)}
        />

        <WithdrawTypeSelect
          withdrawInitial={withdrawInitial}
          selectedCoin={selectedCoin}
        />

        <WithdrawNetworkAddress
          withdrawInitial={withdrawInitial}
          selectedNetwork={selectedNetwork}
          currentNetworkFields={currentNetworkFields}
        />

        <WithdrawAmount
          withdrawAmountMax={withdrawAmountMax}
          selectedCoin={selectedCoin}
          withdrawInitial={withdrawInitial}
          currentNetworkFields={currentNetworkFields}
        />

        <WithdrawSubmit
          getWithdrawInitial={getWithdrawInitial}
          selectedNetwork={selectedNetwork}
          withdrawAmountMax={withdrawAmountMax}
          selectedCoin={selectedCoin}
          currentNetworkFields={currentNetworkFields}
        />
      </Grid>
    </>
  );
}

export default WithdrawForm;
